/**
 * Pure view-model for the Hour Optimizer chart: each of the 24 hours with
 * its bar heights (score relative to the best hour, volume relative to the
 * busiest), its place in the container's span and in the country's window
 * (user, 12/10/2026: see whether the best hours fall in it), the slots and optimizer
 * drops that fall in it, and the plain-words labels the stats card shows.
 * Unranked hours get NO score bar — never drawn as bad.
 * For Media Devoted | Created By Ciro Autiero
 */
import { inCountryWindow, type CountryWindow, type DayPlanOptimizerMark, type DropGenHourBucket, type DropGenHourProfile } from '@trafficpulse/shared';

import { excludeHit } from '@/features/drop-generator/components/editor/rules/rule-model';

import { localDateInTz } from '@trafficpulse/shared';

import { dkLabel } from '../../../../utils/clock/dk-label';

export interface HourView extends DropGenHourBucket {
  /** 0..100 — this hour's score against the best ranked hour. */
  scorePct: number;
  /** 0..100 — unique visits against the busiest hour. */
  volumePct: number;
  /** Why it is unranked, in the exclude rule's words ("Unique Visits 150 < 200 over last 14 days"); null when ranked. */
  excludedWhy: string | null;
  inSpan: boolean;
  /** Any minute of the hour is inside the country's window (true when the window is unknown). */
  inWindow: boolean;
  /** Drawn in colour: the container's hours — or, with no container (a master config), the window's. */
  lit: boolean;
  /** Why it is grey, in words ("outside its hours" / "outside the window"); null when lit. */
  outsideLabel: string | null;
  /** Rank among the ranked hours this container can use (1 = where an extra goes first); null outside or unranked. */
  spanRank: number | null;
  /** Planned regular slot minutes inside this hour (recipient-local). */
  slots: number[];
  /** The optimizer's planned drop in this hour, from the saved day plan. */
  extra: DayPlanOptimizerMark['kind'] | null;
}

export interface SpanInput {
  startMin: number | null;
  endMin: number | null;
  slotMinutes: number[];
  extras: Array<{ minute: number; kind: DayPlanOptimizerMark['kind'] }>;
  /** The country's window, recipient-local minutes; null/absent = unknown. */
  window?: CountryWindow | null;
}

export type { CountryWindow };

/** Hours (0..23) with any minute inside the window — the planner's own rule
 *  (shared inCountryWindow: wraps past midnight, start = end = the whole day). */
export function windowHours(w: CountryWindow): Set<number> {
  const out = new Set<number>();
  for (let h = 0; h < 24; h += 1)
    if (Array.from({ length: 60 }, (_, m) => h * 60 + m).some((m) => inCountryWindow(m, w))) out.add(h);
  return out;
}

/** Hours (0..23) a container may use: [startMin, endMin], wrapping past midnight.
 *  endMin = the container's end, else its last regular drop — as the planner does. */
export function eligibleHours(startMin: number | null, endMin: number | null): Set<number> {
  if (startMin === null) return new Set();
  const start = Math.floor(startMin / 60) % 24;
  const end = endMin === null ? 23 : Math.floor(Math.min(endMin, 2879) / 60) % 24;
  const out = new Set<number>();
  for (let h = start; ; h = (h + 1) % 24) {
    out.add(h);
    if (h === end || out.size === 24) break;
  }
  return out;
}

export function chartModel(profile: DropGenHourProfile, span: SpanInput): HourView[] {
  const ranked = profile.buckets.filter((b) => !b.unranked && b.score !== null).map((b) => b.score ?? 0);
  // Bars read against the best hour; a negative score (ROI) shifts the floor
  // so the worst hour still draws instead of disappearing.
  const floor = Math.min(0, ...ranked);
  const best = Math.max(0, ...ranked) - floor;
  const busiest = Math.max(1, ...profile.buckets.map((b) => b.uniqueVisits));
  const eligible = eligibleHours(span.startMin, span.endMin);
  const inWindow = span.window ? windowHours(span.window) : null;
  const hasContainer = span.startMin !== null;
  const hourOf = (minute: number) => Math.floor((((minute % 1440) + 1440) % 1440) / 60);
  // The window is a hard limit: an hour outside it is never the container's to use.
  const usable = profile.buckets.filter((b) => eligible.has(b.hour) && (!inWindow || inWindow.has(b.hour)) && b.rank !== null).sort((a, b) => a.rank! - b.rank!);
  const spanRank = new Map(usable.map((b, i) => [b.hour, i + 1]));
  return profile.buckets.map((b) => ({
    ...b,
    spanRank: spanRank.get(b.hour) ?? null,
    scorePct: b.unranked || b.score === null || best <= 0 ? 0 : Math.round(((b.score - floor) / best) * 100),
    volumePct: Math.round((b.uniqueVisits / busiest) * 100),
    excludedWhy: b.unranked ? (excludeHit(b.ruleHits)?.reason ?? 'no data in the formula’s ranges') : null,
    inSpan: eligible.has(b.hour),
    ...litFields(hasContainer, eligible.has(b.hour), inWindow ? inWindow.has(b.hour) : true),
    slots: span.slotMinutes.filter((m) => hourOf(m) === b.hour).map((m) => ((m % 1440) + 1440) % 1440),
    extra: span.extras.find((e) => hourOf(e.minute) === b.hour)?.kind ?? null,
  }));
}

/** Lit = a drop may go there: inside the window (a hard limit) and, for a container, its hours. */
function litFields(hasContainer: boolean, inSpan: boolean, inWindow: boolean): Pick<HourView, 'inWindow' | 'lit' | 'outsideLabel'> {
  const lit = inWindow && (!hasContainer || inSpan);
  if (lit) return { inWindow, lit, outsideLabel: null };
  return { inWindow, lit, outsideLabel: inWindow ? 'outside its hours' : 'outside the window' };
}

/** The hour the card shows when nothing is hovered: the day's best hour,
 *  else the busiest, else (no traffic at all) the container's first hour. */
export function defaultHour(rows: HourView[]): number {
  const ranked = rows.filter((r) => r.rank !== null).sort((a, b) => a.rank! - b.rank!);
  if (ranked.length > 0) return ranked[0].hour;
  const busiest = rows.reduce((best, r) => (r.uniqueVisits > best.uniqueVisits ? r : best), rows[0]);
  if (busiest.uniqueVisits > 0) return busiest.hour;
  return rows.find((r) => r.inSpan)?.hour ?? 0;
}

/** Where a recipient-local minute sits on a 0..24h axis, in percent. */
export const axisPct = (minute: number) => ((((minute % 1440) + 1440) % 1440) / 1440) * 100;

export const hourRange = (h: number) => `${pad(h)}:00–${pad((h + 1) % 24)}:00`;

/** The same hour on the Danish clock for the day of `now` ("22:00–23:00"); null without a timezone. */
export function cphHourRange(h: number, timezone: string | null, now: Date = new Date()): string | null {
  if (!timezone) return null;
  const runDate = localDateInTz(now, timezone);
  const from = dkLabel(h * 60, timezone, runDate);
  const to = dkLabel(h * 60 + 60, timezone, runDate);
  return from && to ? `${from.slice(0, 5)}–${to.slice(0, 5)}` : null;
}

/** The Danish hour ("22") of a country hour, today. */
export const dkHour = (h: number, timezone: string | null): string => (timezone ? (dkLabel(h * 60, timezone) ?? '').slice(0, 2) : '');
export const minuteLabel = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
export const count = (n: number) => n.toLocaleString('en-US');
export const money = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Formula scores are raw numbers in the metrics' own units (CR in %, EPC
 *  in money) — shown as they are, two decimals. */
export function scoreLabel(score: number | null): string {
  if (score === null) return '—';
  return score.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 0.35..0.95 — the best hour is the strongest; the last ranked one still shows. */
export function shadeFor(rank: number | null, rankedHours: number): number {
  if (rank === null || rankedHours <= 0) return 0;
  if (rankedHours === 1) return 0.95;
  return 0.95 - ((rank - 1) / (rankedHours - 1)) * 0.6;
}

const pad = (n: number) => String(n).padStart(2, '0');
